import { ChevronDownIcon } from '@langchain/untitled-ui-icons';

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, ButtonGroup, IconButton } from '@/design-system';
import { useOrganizationId, useRepos, useSelectedTenant } from '@/hooks/useSwr';
import ForkIcon from '@/icons/ForkIcon.svg?react';
import { addQueryParams } from '@/utils/add-query-params';
import { appHubIndexPath, appPromptsIndexPath } from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { ForkPromptDialog } from './ForkPromptDialog';

function HubForkButtonV2({ inHub }: { inHub: boolean }) {
  const { owner, repo } = useParams();
  const organizationId = useOrganizationId();
  const { data: selectedTenant } = useSelectedTenant();

  const [open, setOpen] = React.useState(false);
  const [isForkModalOpen, setIsForkModalOpen] = React.useState(false);

  // get list of forks relevant to me
  const { data: forks } = useRepos({
    upstream_repo_handle: repo,
    upstream_repo_owner: owner ?? selectedTenant?.tenant_handle ?? undefined,
    tenant_handle: selectedTenant?.tenant_handle ?? undefined,
    ...(!owner && { tenant_id: selectedTenant?.id }),
  });

  const mainForkButton = (
    <Button
      size="md"
      color="secondary"
      variant="outlined"
      leftDecorator={ForkIcon}
      onClick={() => setIsForkModalOpen(true)}
    >
      Fork
    </Button>
  );

  return (
    <>
      {forks?.repos?.length ? (
        <Popover open={open} onOpenChange={setOpen}>
          <ButtonGroup variant="outlined" color="secondary" size="md">
            {mainForkButton}
            <PopoverTrigger asChild>
              <IconButton icon={ChevronDownIcon} label="Show existing forks" />
            </PopoverTrigger>
          </ButtonGroup>
          <PopoverContent align="end" className="w-64 p-0">
            <div className="border-b border-secondary px-3 py-2 text-xs font-semibold uppercase text-tertiary">
              Existing
            </div>
            <div className="flex flex-col">
              {forks?.repos?.map?.((option, index) => (
                <Link
                  key={option.id}
                  to={addQueryParams(
                    `/${
                      inHub && option.is_public
                        ? `${appHubIndexPath}/${option.full_name}`
                        : `${appPromptsIndexPath}/${option.repo_handle}`
                    }`,
                    {
                      organizationId,
                    }
                  )}
                  onClick={() => setOpen(false)}
                >
                  <Button
                    className={cn(
                      'w-full',
                      index > 0 &&
                        'border-x-0 border-b-0 border-t border-secondary',
                      index !== forks.repos.length - 1
                        ? 'rounded-none'
                        : 'rounded-t-none'
                    )}
                    size="md"
                    variant="plain"
                  >
                    {inHub && option.is_public
                      ? option.full_name
                      : option.repo_handle}
                  </Button>
                </Link>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        mainForkButton
      )}
      <ForkPromptDialog
        open={isForkModalOpen}
        onClose={() => setIsForkModalOpen(false)}
        inHub={inHub}
      />
    </>
  );
}

export function HubForkButton({ inHub }: { inHub: boolean }) {
  return <HubForkButtonV2 inHub={inHub} />;
}
